/**
 * Which install story a browser gets for the lokerdollar extension.
 *
 * - `installable` — desktop Chromium that installs from the Chrome Web Store:
 *   Chrome, Edge and Brave. Brave ships a Chrome-identical UA (no `Brave/`
 *   token), and Edge installs Web Store extensions after one "allow other
 *   stores" prompt, so both belong here. The old `isChrome()` check excluded
 *   them and still showed the store link on phones, where no browser can
 *   install it (24% of banner impressions in the 30 days to 2026-10-05).
 * - `mobile` — any phone or tablet. The useful action there is to send the
 *   link to a laptop, never the store link.
 * - `unsupported` — desktop Safari, Firefox, Opera and anything unrecognised.
 */
export type ExtensionBrowserSupport = "installable" | "mobile" | "unsupported";

export const EXTENSION_STORE_URL =
	"https://chromewebstore.google.com/detail/lokerdollar-%E2%80%94-one-click-a/mchfpcjbjiimffjoeakpcplakmmdinbb";

const MOBILE_UA =
	/Android|iPhone|iPad|iPod|Mobile|CriOS|FxiOS|EdgiOS|SamsungBrowser|Silk|Kindle|Opera Mini/i;

export function classifyExtensionBrowser(
	userAgent: string,
	/** `navigator.userAgentData.mobile` when the browser exposes it. */
	uaDataMobile?: boolean,
): ExtensionBrowserSupport {
	if (uaDataMobile === true || MOBILE_UA.test(userAgent)) return "mobile";
	// iPadOS 13+ reports a desktop Safari UA; it still cannot install.
	if (!/Chrome\//.test(userAgent)) return "unsupported";
	// Opera and Yandex are Chromium too, but we have not verified the install
	// path there, so they get no store link.
	if (/OPR\/|YaBrowser\//.test(userAgent)) return "unsupported";
	return "installable";
}

/** Client-only. Returns `null` during SSR so callers render nothing. */
export function detectExtensionBrowserSupport(): ExtensionBrowserSupport | null {
	if (typeof navigator === "undefined") return null;
	const uaData = (
		navigator as Navigator & { userAgentData?: { mobile?: boolean } }
	).userAgentData;
	return classifyExtensionBrowser(navigator.userAgent, uaData?.mobile);
}
